import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWidgetVisibility } from '../../context/WidgetVisibilityContext';
import { bedService, floorService, roomService, tenantService } from '../../services/api';
import { Bed, Floor, Room, User } from '../../types';
import { formatFloorShortLabel } from '../../utils/locationLabels';

type PropertyView = 'floors' | 'rooms' | 'beds';

function statusUpper(b: Bed): string {
  return String(b.bedStatus || 'AVAILABLE').toUpperCase();
}

function isVacatingTenant(t: User): boolean {
  return Boolean(t.isNotice) && !t.isDeleted;
}

function bedIsVacating(
  b: Bed,
  vacatingTenantIds: Set<number>,
  noticeBedIds: Set<number>,
): boolean {
  const occId = (b.occupant as { id?: number } | undefined)?.id;
  if (occId != null && vacatingTenantIds.has(occId)) return true;
  return noticeBedIds.has(b.id);
}

function countVacatingBeds(
  bedList: Bed[],
  vacatingTenantIds: Set<number>,
  noticeBedIds: Set<number>,
): number {
  return bedList.filter((b) => bedIsVacating(b, vacatingTenantIds, noticeBedIds)).length;
}

const floorLabel = formatFloorShortLabel;

const ReportsProperty: React.FC = () => {
  const { hostel } = useAuth();
  const { isVisible } = useWidgetVisibility();
  const [loading, setLoading] = useState(true);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [tenants, setTenants] = useState<User[]>([]);
  const [view, setView] = useState<PropertyView>('floors');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!hostel?.id) {
      setLoading(false);
      return;
    }
    const hid = hostel.id;
    void (async () => {
      try {
        setLoading(true);
        const [f, r, b, t] = await Promise.all([
          floorService.getFloorsByHostel(hid),
          roomService.getRoomsByHostel(hid),
          bedService.getBedsByHostel(hid),
          tenantService.getTenantsByHostel(hid, 'ALL_USERS'),
        ]);
        setFloors(f.filter((x) => x.isActive !== false));
        setRooms(r.filter((x) => x.isActive !== false));
        setBeds(b.filter((x) => x.isActive !== false));
        setTenants(t.filter((x) => !x.isDeleted));
      } catch {
        setFloors([]);
        setRooms([]);
        setBeds([]);
        setTenants([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [hostel?.id]);

  const vacatingTenantIds = useMemo(
    () => new Set(tenants.filter(isVacatingTenant).map((t) => t.id)),
    [tenants],
  );

  const noticeBedIds = useMemo(
    () =>
      new Set(
        tenants
          .filter(isVacatingTenant)
          .map((t) => t.bedId)
          .filter((id): id is number => id != null && Number.isFinite(Number(id)))
          .map((id) => Number(id)),
      ),
    [tenants],
  );

  const summary = useMemo(() => {
    const occupied = beds.filter((b) => statusUpper(b) === 'OCCUPIED').length;
    const vacant = beds.filter((b) => statusUpper(b) === 'AVAILABLE').length;
    const reserved = beds.filter((b) => statusUpper(b) === 'RESERVED').length;
    const blocked = beds.filter((b) => statusUpper(b) === 'BLOCKED').length;
    const vacating = countVacatingBeds(beds, vacatingTenantIds, noticeBedIds);
    const total = beds.length;
    const occPct = total > 0 ? ((occupied / total) * 100).toFixed(1) : '0';
    return { floors: floors.length, rooms: rooms.length, total, occupied, vacant, reserved, blocked, vacating, occPct };
  }, [beds, floors, rooms, vacatingTenantIds, noticeBedIds]);

  const floorRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return floors
      .map((f) => {
        const floorBeds = beds.filter((b) => Number(b.floorId) === f.id);
        const floorRooms = rooms.filter((r) => Number(r.floorId) === f.id);
        const occupied = floorBeds.filter((b) => statusUpper(b) === 'OCCUPIED').length;
        const vacant = floorBeds.filter((b) => statusUpper(b) === 'AVAILABLE').length;
        const vacatingOnFloor = countVacatingBeds(floorBeds, vacatingTenantIds, noticeBedIds);
        return {
          id: f.id,
          label: floorLabel(f),
          rooms: floorRooms.length,
          beds: floorBeds.length,
          occupied,
          vacant,
          vacating: vacatingOnFloor,
          fillPct: floorBeds.length > 0 ? ((occupied / floorBeds.length) * 100).toFixed(0) : '0',
        };
      })
      .filter((row) => !q || row.label.toLowerCase().includes(q))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [floors, beds, rooms, vacatingTenantIds, noticeBedIds, query]);

  const roomRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const floorById = Object.fromEntries(floors.map((f) => [f.id, f]));
    return rooms
      .map((r) => {
        const roomBeds = beds.filter((b) => Number(b.roomId) === r.id);
        const occupied = roomBeds.filter((b) => statusUpper(b) === 'OCCUPIED').length;
        const vacant = roomBeds.filter((b) => statusUpper(b) === 'AVAILABLE').length;
        const fl = floorById[r.floorId];
        const floorName = fl ? floorLabel(fl) : '—';
        return {
          id: r.id,
          roomNumber: r.roomNumber,
          floorName,
          sharing: r.sharingType || `${r.totalBeds}-share`,
          beds: roomBeds.length || r.totalBeds,
          occupied,
          vacant,
          fillPct: roomBeds.length > 0 ? ((occupied / roomBeds.length) * 100).toFixed(0) : '0',
        };
      })
      .filter(
        (row) =>
          !q ||
          row.roomNumber.toLowerCase().includes(q) ||
          row.floorName.toLowerCase().includes(q),
      )
      .sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }));
  }, [rooms, beds, floors, query]);

  const bedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const roomById = Object.fromEntries(rooms.map((r) => [r.id, r]));
    const floorById = Object.fromEntries(floors.map((f) => [f.id, f]));
    return beds
      .map((b) => {
        const room = roomById[b.roomId];
        const floor = floorById[b.floorId];
        const occupant = b.occupant as { id?: number; username?: string } | undefined;
        const isVacating = bedIsVacating(b, vacatingTenantIds, noticeBedIds);
        const status = statusUpper(b);
        return {
          id: b.id,
          bedNumber: b.bedNumber || b.bedName || String(b.id),
          room: room?.roomNumber ?? '—',
          floor: floor ? floorLabel(floor) : '—',
          status,
          tenant: occupant?.username || (status === 'OCCUPIED' ? 'Occupied' : '—'),
          vacating: isVacating,
        };
      })
      .filter(
        (row) =>
          !q ||
          row.bedNumber.toLowerCase().includes(q) ||
          row.room.toLowerCase().includes(q) ||
          row.floor.toLowerCase().includes(q) ||
          row.tenant.toLowerCase().includes(q),
      )
      .sort((a, b) => a.floor.localeCompare(b.floor) || a.room.localeCompare(b.room));
  }, [beds, rooms, floors, vacatingTenantIds, noticeBedIds, query]);

  if (loading) return <div className="reports-loading">Loading property report…</div>;
  if (!hostel?.id) return <div className="reports-empty">Select a hostel to view property reports.</div>;

  return (
    <div className="reports-section">
      <div className="reports-section__toolbar">
        <div>
          <h2 className="reports-section__heading">Property</h2>
          <p className="reports-section__sub">Floors, rooms, and beds — occupancy, vacancy, and vacating</p>
        </div>
      </div>

      {isVisible('reports.property.summary') && (
      <div className="reports-kpi-grid reports-kpi-grid--compact">
        {[
          { label: 'Floors', value: String(summary.floors) },
          { label: 'Rooms', value: String(summary.rooms) },
          { label: 'Total beds', value: String(summary.total) },
          { label: 'Occupied', value: String(summary.occupied), hint: `${summary.occPct}%` },
          { label: 'Vacant', value: String(summary.vacant) },
          { label: 'Vacating beds', value: String(summary.vacating) },
          { label: 'Reserved', value: String(summary.reserved) },
        ].map((k) => (
          <div key={k.label} className="reports-kpi">
            <span className="reports-kpi__label">{k.label}</span>
            <span className="reports-kpi__value">{k.value}</span>
            {k.hint && <span className="reports-kpi__hint">{k.hint}</span>}
          </div>
        ))}
      </div>
      )}

      {isVisible('reports.property.table') && (
      <>
      <div className="reports-filter-bar">
        <input
          type="search"
          className="reports-search"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="reports-tabs">
          {(
            [
              { id: 'floors' as const, label: 'By floor' },
              { id: 'rooms' as const, label: 'By room' },
              { id: 'beds' as const, label: 'All beds' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`reports-tab${view === tab.id ? ' reports-tab--active' : ''}`}
              onClick={() => setView(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="reports-table-wrap responsive-table-wrap">
        {view === 'floors' && (
          <table className="reports-table responsive-table">
            <thead>
              <tr>
                <th>Floor</th>
                <th>Rooms</th>
                <th>Beds</th>
                <th>Occupied</th>
                <th>Vacant</th>
                <th>Vacating</th>
                <th>Fill %</th>
              </tr>
            </thead>
            <tbody>
              {floorRows.length === 0 ? (
                <tr className="responsive-table__empty">
                  <td colSpan={7} className="reports-table__empty" data-label="">
                    No floors found.
                  </td>
                </tr>
              ) : (
                floorRows.map((row) => (
                  <tr key={row.id}>
                    <td className="responsive-table__primary" data-label="Floor">
                      <strong>{row.label}</strong>
                    </td>
                    <td data-label="Rooms">{row.rooms}</td>
                    <td data-label="Beds">{row.beds}</td>
                    <td className="reports-text--ok" data-label="Occupied">{row.occupied}</td>
                    <td data-label="Vacant">{row.vacant}</td>
                    <td className={row.vacating > 0 ? 'reports-text--warn' : ''} data-label="Vacating">
                      {row.vacating}
                    </td>
                    <td className="responsive-table__full" data-label="Fill %">
                      <span className="reports-fill-bar">
                        <span style={{ width: `${row.fillPct}%` }} />
                      </span>
                      {row.fillPct}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {view === 'rooms' && (
          <table className="reports-table responsive-table">
            <thead>
              <tr>
                <th>Room</th>
                <th>Floor</th>
                <th>Sharing</th>
                <th>Beds</th>
                <th>Occupied</th>
                <th>Vacant</th>
                <th>Fill %</th>
              </tr>
            </thead>
            <tbody>
              {roomRows.length === 0 ? (
                <tr className="responsive-table__empty">
                  <td colSpan={7} className="reports-table__empty" data-label="">
                    No rooms found.
                  </td>
                </tr>
              ) : (
                roomRows.map((row) => (
                  <tr key={row.id}>
                    <td className="responsive-table__primary" data-label="Room">
                      <strong>Room {row.roomNumber}</strong>
                    </td>
                    <td data-label="Floor">{row.floorName}</td>
                    <td data-label="Sharing">{row.sharing}</td>
                    <td data-label="Beds">{row.beds}</td>
                    <td className="reports-text--ok" data-label="Occupied">{row.occupied}</td>
                    <td data-label="Vacant">{row.vacant}</td>
                    <td className="responsive-table__full" data-label="Fill %">
                      <span className="reports-fill-bar">
                        <span style={{ width: `${row.fillPct}%` }} />
                      </span>
                      {row.fillPct}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {view === 'beds' && (
          <table className="reports-table responsive-table">
            <thead>
              <tr>
                <th>Bed</th>
                <th>Room</th>
                <th>Floor</th>
                <th>Status</th>
                <th>Tenant</th>
                <th>Notice</th>
              </tr>
            </thead>
            <tbody>
              {bedRows.length === 0 ? (
                <tr className="responsive-table__empty">
                  <td colSpan={6} className="reports-table__empty" data-label="">
                    No beds found.
                  </td>
                </tr>
              ) : (
                bedRows.map((row) => (
                  <tr key={row.id}>
                    <td className="responsive-table__primary" data-label="Bed">
                      <strong>{row.bedNumber}</strong>
                    </td>
                    <td data-label="Room">{row.room}</td>
                    <td data-label="Floor">{row.floor}</td>
                    <td data-label="Status">
                      <span className={`reports-bed-status reports-bed-status--${row.status.toLowerCase()}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="responsive-table__full" data-label="Tenant">{row.tenant}</td>
                    <td data-label="Notice">
                      {row.vacating ? (
                        <span className="reports-badge reports-badge--warn">Vacating</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default ReportsProperty;
